import { useEffect, useState } from "react";
import { getAuthToken, SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";
import { ClickableDate } from "./ClickableDate";
import { useIsMobile } from "../lib/useIsMobile";
import { localDateStr } from "../lib/date";

export function BlockOffDatesEditor({ showToast, onRefresh }) {
  const isMobile = useIsMobile();
  const [dates,   setDates]   = useState([]);
  const [newDate, setNewDate] = useState("");
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(()=>{ loadDates(); },[]);

  async function loadDates() {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/block_off_dates?order=date.asc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json());
    if (Array.isArray(res)) setDates(res.sort((a,b)=>a.date.localeCompare(b.date)));
  }

  const authHeaders = (extra={}) => ({ apikey:SUPABASE_ANON_KEY, Authorization:`Bearer ${getAuthToken()}`, ...extra });
  const todayStr = () => localDateStr();

  async function addDate() {
    if (!newDate) return;
    // Look at what's already scheduled on this day BEFORE blocking it. Only the
    // recurring (auto) visits should be removed — deleting a MANUAL one-off (charged
    // at creation) or a visit RESCHEDULED onto this date destroys paid/planned work
    // with no credit or notice (audit S4/S6). Those are preserved + flagged instead.
    let recurring = [], protectedJobs = [];
    if (newDate >= todayStr()) {
      const jobs = await fetch(`${SUPABASE_URL}/rest/v1/jobs?scheduled_date=eq.${newDate}&status=eq.scheduled&select=id,client_name,source`,{headers:authHeaders()}).then(r=>r.json()).catch(()=>[]);
      if (Array.isArray(jobs)) {
        protectedJobs = jobs.filter(j => j.source==="manual" || j.source==="reschedule");
        recurring     = jobs.filter(j => !(j.source==="manual" || j.source==="reschedule"));
      }
    }
    if (protectedJobs.length) {
      const names = protectedJobs.map(j=>j.client_name).filter(Boolean).slice(0,5).join(", ");
      if (!window.confirm(`${protectedJobs.length} one-off or rescheduled visit${protectedJobs.length!==1?"s":""} on ${newDate}${names?` (${names}${protectedJobs.length>5?"…":""})`:""} will be KEPT — a charged one-off shouldn't be deleted with the payment kept. Block the day anyway? Reschedule or refund those clients from their profiles.`)) return;
    }
    setLoading(true);
    await fetch(`${SUPABASE_URL}/rest/v1/block_off_dates`,{method:"POST",headers:authHeaders({"Content-Type":"application/json",Prefer:"return=minimal"}),body:JSON.stringify({ date:newDate, note:newNote.trim()||null })});
    // Remove ONLY the recurring auto visits (a tech shouldn't be dispatched to a
    // closed day). Manual/rescheduled visits are preserved + flagged above.
    let removed = 0;
    if (recurring.length) {
      const del = await fetch(`${SUPABASE_URL}/rest/v1/jobs?id=in.(${recurring.map(j=>j.id).join(",")})`,{method:"DELETE",headers:authHeaders({Prefer:"return=representation"})});
      const rows = await del.json().catch(()=>[]);
      removed = Array.isArray(rows) ? rows.length : 0;
    }
    setNewDate(""); setNewNote("");
    await loadDates();
    showToast(`${newDate} blocked off${removed>0?` — ${removed} recurring visit${removed!==1?"s":""} removed`:""}${protectedJobs.length?` · ${protectedJobs.length} one-off/rescheduled kept — handle manually`:""}`);
    onRefresh && onRefresh();
    setLoading(false);
  }

  async function removeDate(id, date) {
    // A mis-tap here deletes the block-off AND regenerates that day's visits — confirm first.
    if (!window.confirm(`Unblock ${date}?${date>=todayStr()?" Recurring visits for that day will be scheduled again.":""}`)) return;
    await fetch(`${SUPABASE_URL}/rest/v1/block_off_dates?id=eq.${id}`,{method:"DELETE",headers:authHeaders()});
    // S5: un-blocking a future date must rebuild the visits it had suppressed,
    // otherwise that day of service silently stays empty. Regenerate its month
    // (idempotent; from_date=today avoids backfilling past dates).
    if (date >= todayStr()) {
      const [yy,mm] = date.split("-");
      await fetch(`${SUPABASE_URL}/functions/v1/generate-monthly-jobs`,{method:"POST",headers:authHeaders({"Content-Type":"application/json"}),body:JSON.stringify({ year:Number(yy), month:Number(mm)-1, from_date:todayStr() })}).catch(e=>console.error("regen after unblock failed:",e));
    }
    await loadDates();
    showToast(`${date} unblocked${date>=todayStr()?" — visits restored":""}`);
    onRefresh && onRefresh();
  }

  const upcoming = dates.filter(d=>d.date>=localDateStr());
  const past     = dates.filter(d=>d.date< localDateStr());

  // Phone: the unblock × is a 40px target pinned to the right of the row (desktop keeps the bare glyph).
  const removeBtn = (color, size) => isMobile
    ? {background:"none",border:"none",cursor:"pointer",color,fontSize:20,lineHeight:1,width:40,height:40,display:"inline-flex",alignItems:"center",justifyContent:"center",borderRadius:10,marginLeft:"auto",flexShrink:0,padding:0}
    : {background:"none",border:"none",cursor:"pointer",color,fontSize:size};

  return (
    <div>
      {/* Phone: the three controls stack as one intentional full-width form instead of a
          160px chip floating on its own line (iOS date inputs also overflow a fixed width). */}
      <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap",flexDirection:isMobile?"column":"row"}}>
        <ClickableDate value={newDate} onChange={setNewDate} width={isMobile?"100%":160} style={isMobile?{minHeight:44,boxSizing:"border-box"}:undefined}/>
        <input value={newNote} onChange={e=>setNewNote(e.target.value)} placeholder="Reason (optional)" style={isMobile?{width:"100%",minWidth:0}:{flex:1,minWidth:160}}/>
        <button className="btn btn-gold" onClick={addDate} disabled={!newDate||loading} style={isMobile?{width:"100%"}:undefined}>{loading?"Adding...":"+ Block Date"}</button>
      </div>
      {upcoming.length===0&&<div style={{fontSize:12,color:"var(--text2)",marginBottom:12}}>No upcoming blocked dates.</div>}
      {upcoming.map(d=>(
        <div key={d.id} style={{display:"flex",alignItems:"center",gap:10,padding:isMobile?"4px 4px 4px 12px":"8px 12px",background:"#ffebe6",borderRadius:6,marginBottom:6,border:"1px solid #ffbdad"}}>
          <span style={{fontWeight:700,fontSize:13,color:"#bf2600",flexShrink:0}}>🚫 {new Date(d.date+"T12:00:00").toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}</span>
          {d.note&&<span style={{fontSize:12,color:"var(--text2)",flex:1,minWidth:0}}>{d.note}</span>}
          <button onClick={()=>removeDate(d.id,d.date)} aria-label={`Unblock ${d.date}`} style={removeBtn("#bf2600",16)}>×</button>
        </div>
      ))}
      {past.length>0&&(
        <div style={{marginTop:12}}>
          <div style={{fontSize:11,color:"var(--text2)",marginBottom:6}}>Past blocked dates</div>
          {past.slice(-3).map(d=>(
            <div key={d.id} style={{display:"flex",alignItems:"center",gap:10,padding:isMobile?"2px 4px 2px 12px":"6px 12px",background:"var(--border)",borderRadius:6,marginBottom:4,opacity:.6}}>
              <span style={{fontSize:12,color:"var(--text2)",flexShrink:0}}>🚫 {d.date}</span>
              {d.note&&<span style={{fontSize:11,color:"var(--text2)",flex:1,minWidth:0}}>{d.note}</span>}
              <button onClick={()=>removeDate(d.id,d.date)} aria-label={`Remove ${d.date}`} style={removeBtn("var(--text2)",14)}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
